import React from 'react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">About JobMatch</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Revolutionizing the job search experience with AI-powered matching technology
            that connects talented professionals with their dream opportunities.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Our Mission
            </h2>
            <p className="text-lg text-gray-700 text-center">
              At JobMatch, we believe that finding the right job should be simple, efficient,
              and personalized. Our mission is to empower job seekers and employers with
              cutting-edge AI technology that makes better matches, faster.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-12 text-center">
            What Makes Us Different
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="text-5xl mb-4">🤖</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                AI-Powered Matching
              </h3>
              <p className="text-gray-600">
                Our advanced algorithms analyze skills, experience, culture fit, and more
                to find your perfect match.
              </p>
            </div>

            <div className="text-center">
              <div className="text-5xl mb-4">⚡</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                Lightning Fast
              </h3>
              <p className="text-gray-600">
                Get matched with relevant opportunities in seconds, not weeks. Our platform
                works 24/7 to find the best fits.
              </p>
            </div>

            <div className="text-center">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                Personalized Experience
              </h3>
              <p className="text-gray-600">
                Every user gets a customized experience based on their unique profile,
                preferences, and career goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-12 text-center">
            Our Impact
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">10,000+</div>
              <div className="text-gray-600">Active Job Seekers</div>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
              <div className="text-gray-600">Partner Companies</div>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">95%</div>
              <div className="text-gray-600">Match Success Rate</div>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">50+</div>
              <div className="text-gray-600">Industries Covered</div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            Our Story
          </h2>
          <div className="space-y-6 text-gray-700 text-lg">
            <p>
              JobMatch was founded in 2020 by a team of tech enthusiasts and HR professionals
              who experienced firsthand the frustrations of traditional job searching and recruiting.
            </p>
            <p>
              We noticed that despite the digital age, the job matching process was still largely
              manual, time-consuming, and often resulted in poor fits. We knew there had to be a
              better way.
            </p>
            <p>
              By combining artificial intelligence, machine learning, and deep industry knowledge,
              we created a platform that truly understands what makes a great job match. Today,
              we're proud to help thousands of job seekers and companies find their perfect match
              every day.
            </p>
          </div>
        </div>
      </section>

      {/* Team Values */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            Our Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Innovation</h3>
              <p className="text-gray-600">
                We continuously push the boundaries of what's possible in recruitment technology.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Transparency</h3>
              <p className="text-gray-600">
                We believe in clear communication and honest practices with all our users.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Inclusivity</h3>
              <p className="text-gray-600">
                We're committed to creating equal opportunities for all job seekers.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Excellence</h3>
              <p className="text-gray-600">
                We strive for excellence in everything we do, from our technology to our support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Find Your Perfect Match?</h2>
          <p className="text-xl mb-8">
            Join thousands of professionals who've already found their dream jobs with JobMatch
          </p>
          <Link
            to="/register"
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition inline-block"
          >
            Get Started Today
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
